import React, { useEffect, useState, useRef, useContext } from 'react';
import { bool, array, string, func, node, oneOf } from 'prop-types';
import { createPortal } from 'react-dom';
import cx from 'classnames';
import useStyles from 'hooks/useStyles';

import ContextStore from 'utils/createContext';
import ApplicationContext from 'components/ApplicationContext';
import { ChevronIcon, CloseIcon } from 'components/Icon';
import useClickElementOutSide from 'hooks/useClickElementOutSide';
import { DEFAULT, TYPE_LIST } from 'constants/dropdownButtonType';

import styles from './DropdownButton.scss';

function DropdownButton({
  className,
  isDisabled,
  dropdownList,
  buttonType,
  renderDropdownItemComponent,
  onClick,
  children,
}) {
  useStyles(styles);

  const {
    screenWidth: { isMobile },
  } = useContext(ApplicationContext);

  const [showDropdown, setShowDropdown] = useState(false);
  const toggleDropdown = () => setShowDropdown(prev => !prev);
  const dropdownButtonRef = useRef(null);

  useEffect(() => {
    document.body.style.overflow = isMobile && showDropdown ? 'hidden' : 'auto';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isMobile, showDropdown]);

  const dropdownRef = useClickElementOutSide(() => {
    if (!showDropdown) return;
    toggleDropdown();
  });

  const onClickDropdownButton = event => {
    event.stopPropagation();
    if (typeof onClick === 'function') onClick();
    toggleDropdown();
  };

  const onClickDropdownContent = event => event.stopPropagation();

  const renderDropdown = () => {
    const {
      left,
      y,
      x,
      height,
      width,
    } = dropdownButtonRef.current.getBoundingClientRect();

    const { clientWidth } = document.body;
    const isAtOverHalf = x > clientWidth / 2;

    const dropdownPositions = isMobile
      ? {
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
        }
      : {
          top: y + height,
          ...(isAtOverHalf && {
            right: document.body.clientWidth - (left + width),
          }),
          ...(!isAtOverHalf && {
            left,
          }),
        };

    return createPortal(
      <div className={cx(styles.dropdownWrapper)} style={dropdownPositions}>
        <div onClick={onClickDropdownContent}>
          <ul className={cx(styles.dropdown)} ref={dropdownRef}>
            {isMobile && (
              <li className={styles.bottomDrawerWrapperClose}>
                <CloseIcon
                  className={styles.close}
                  width={24}
                  height={24}
                  onClick={toggleDropdown}
                />
              </li>
            )}
            {dropdownList.map(renderDropdownItemComponent)}
          </ul>
        </div>
      </div>,
      document.querySelector('#modal'),
    );
  };

  return (
    <div
      className={cx(styles.root, className, {
        [styles.defaultType]: buttonType === DEFAULT,
        [styles.isDisabled]: isDisabled,
      })}
      ref={dropdownButtonRef}
      onClick={onClickDropdownButton}
    >
      {children}
      <ChevronIcon
        width={16}
        height={16}
        className={cx(styles.icon, styles.arrow, {
          [styles.dropdownArrow]: showDropdown,
        })}
      />
      <ContextStore.Provider
        value={{
          toggleDropdown,
        }}
      >
        {showDropdown && renderDropdown()}
      </ContextStore.Provider>
    </div>
  );
}

DropdownButton.propTypes = {
  dropdownList: array.isRequired,
  renderDropdownItemComponent: func.isRequired,
  children: node.isRequired,
  className: string,
  buttonType: oneOf(TYPE_LIST),
  isDisabled: bool,
  onClick: func,
};

DropdownButton.defaultProps = {
  className: '',
  buttonType: DEFAULT,
  isDisabled: false,
  onClick: undefined,
};

export default DropdownButton;
